import React from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { 
  HomeIcon, 
  UsersIcon, 
  BuildingOfficeIcon, 
  ListBulletIcon, 
  ArrowDownTrayIcon, 
  Cog6ToothIcon, 
  QuestionMarkCircleIcon, 
  ArrowRightOnRectangleIcon,
  SparklesIcon,
  PuzzlePieceIcon,
  WalletIcon,
  ChevronDownIcon
} from "@heroicons/react/24/outline";
import { RocketLaunchIcon } from "@heroicons/react/24/solid";
import { useAuthStore } from "../../../store/useAuthStore";
import { useLogout } from "../../../features/auth/api/auth";

const navItems = [
  { name: "Home", path: "/", icon: HomeIcon },
  { name: "People", path: "/people", icon: UsersIcon },
  { name: "Companies", path: "/companies", icon: BuildingOfficeIcon },
  { name: "Lists", path: "/lists", icon: ListBulletIcon },
];

export default function DashboardLayout() {
  const location = useLocation();
  const { user } = useAuthStore();
  const { mutate: logout } = useLogout();

  const getInitials = (name?: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <div className="flex h-screen bg-surface-50 font-sans overflow-hidden">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col justify-between hidden md:flex">
        <div>
          <div className="h-16 flex items-center px-6 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-brand-900 rounded-md flex items-center justify-center">
                <span className="text-white font-bold">L</span>
              </div>
              <div>
                <h1 className="text-sm font-bold text-slate-900 leading-tight">Director Discovery</h1>
                {/* <p className="text-[10px] text-slate-500 uppercase tracking-wide">Director Discovery</p> */}
              </div>
            </div>
          </div>
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.name} to={item.path} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-brand-50 text-brand-900 border-l-4 border-brand-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                  <item.icon className="w-5 h-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="p-4 space-y-2 border-t border-slate-200">
          <button className="w-full flex items-center justify-center gap-2 bg-brand-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-950 transition-colors">
            <ArrowDownTrayIcon className="w-4 h-4" /> Export Leads
          </button>
          <button className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">
            <Cog6ToothIcon className="w-5 h-5" /> Settings
          </button>
          <button className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">
            <QuestionMarkCircleIcon className="w-5 h-5" /> Help
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Topbar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 shrink-0 relative z-20">
          <div className="w-96">
            <div className="relative">
              <input type="text" placeholder="Search leads, companies, or lists..." className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:bg-white transition-colors" />
              <svg className="w-4 h-4 text-slate-400 absolute left-3 top-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Existing Action Buttons */}
            <div className="flex items-center gap-2 mr-2">
              <button className="text-brand-900 bg-brand-50 px-3 py-1.5 rounded-lg text-sm font-semibold hover:bg-brand-100 transition-colors">Bulk Import</button>
              <button className="bg-brand-900 text-white px-3 py-1.5 rounded-lg text-sm font-semibold hover:bg-brand-950 transition-colors">+ New Lead</button>
            </div>

            {/* New Upgrade Button */}
            <button className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-md text-sm font-semibold hover:bg-red-100 transition-colors">
              <SparklesIcon className="w-4 h-4" /> Upgrade
            </button>

            <div className="h-6 w-px bg-slate-200" aria-hidden="true" />

            {/* Utility Icons */}
            {/* <div className="flex items-center gap-3 text-slate-600">
              <button className="hover:text-slate-900 transition-colors"><QuestionMarkCircleIcon className="w-5 h-5" /></button>
              <button className="hover:text-slate-900 transition-colors"><PuzzlePieceIcon className="w-5 h-5" /></button>
              <button className="hover:text-slate-900 transition-colors"><WalletIcon className="w-5 h-5" /></button>
            </div> */}

            {/* Profile Hover Dropdown */}
            <div className="relative group h-16 flex items-center cursor-pointer ml-1">
              {/* Visible Header Trigger */}
              <div className="flex items-center gap-1 pl-2 hover:bg-slate-50 rounded-full py-1 pr-1 transition-colors">
                <div className="w-8 h-8 rounded-full bg-red-50 text-red-600 font-bold text-xs flex items-center justify-center border border-red-100">
                  {getInitials(user?.name)}
                </div>
                <ChevronDownIcon className="w-4 h-4 text-slate-500" />
              </div>

              {/* Dropdown Menu */}
              <div className="absolute right-0 top-full w-72 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="bg-white rounded-xl shadow-xl border border-slate-100 p-2 flex flex-col">
                  
                  {/* User Info Row */}
                  <div className="flex items-center gap-3 px-3 py-3">
                    <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 font-bold text-sm flex items-center justify-center border border-red-100 shrink-0">
                      {getInitials(user?.name)}
                    </div>
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="font-semibold text-slate-900 text-base">{user?.name || "John Doe"}</span>
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-semibold rounded-md border border-blue-100">Free</span>
                    </div>
                  </div>

                  {/* Upgrade Banner */}
                  <div className="mx-2 mb-2 bg-red-50 border border-red-100 rounded-lg p-3 flex items-center justify-between cursor-pointer hover:bg-red-100 transition-colors">
                    <div className="flex items-center gap-2 text-red-600 font-semibold text-sm">
                      <RocketLaunchIcon className="w-4 h-4" /> Upgrade 
                    </div>
                    <span className="bg-red-600 text-white text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded-md shadow-sm">
                      Free Account
                    </span>
                  </div>

                  <div className="h-px bg-slate-100 my-1 mx-2" />

                  {/* Links */}
                  <div className="p-1">
                    <button className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-brand-900 rounded-md transition-colors">
                      <Cog6ToothIcon className="w-4 h-4" /> Account & Settings
                    </button>
                    <button onClick={() => logout()} className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-red-50 hover:text-red-600 rounded-md transition-colors">
                      <ArrowRightOnRectangleIcon className="w-4 h-4" /> Log out
                    </button>
                  </div>

                </div>
              </div>
            </div>

          </div>
        </header>

        {/* Dynamic Page Content */}
        <main className="flex-1 overflow-y-auto bg-surface-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}